<template>
  <div class="dynamic-form">
    <template v-for="(field, idx) in fields">
      <FieldText
        v-if="field.class === 'text'"
        :key="field.value + idx"
        :field="field"
        v-model="formData[field.value]"
      />
      <FieldNumber
        v-else-if="field.class === 'number'"
        :key="field.value + idx"
        :field="field"
        v-model="formData[field.value]"
      />
      <FieldRadio
        v-else-if="field.class === 'radio'"
        :key="field.value + idx"
        :field="field"
        v-model="formData[field.value]"
      />
      <FieldCheckbox
        v-else-if="field.class === 'checkbox'"
        :key="field.value + idx"
        :field="field"
        v-model="formData[field.value]"
      />
      <FieldDatetime
        v-else-if="field.class === 'datetime'"
        :key="field.value + idx"
        :field="field"
        v-model="formData[field.value]"
      />
      <FieldSelcet
        v-else-if="field.class === 'selcet'"
        :key="field.value + idx"
        :ref="`field_${field.value}`"
        :field="field"
        v-model="formData[field.value]"
      />
      <FieldFileList
        v-else-if="field.class === 'fileList'"
        :key="field.value + idx"
        :ref="`field_${field.value}`"
        :field="field"
        v-model="formData[field.value]"
      />
      <FieldFile
        v-else-if="field.class === 'file'"
        :key="field.value + idx"
        :ref="`field_${field.value}`"
        :field="field"
        v-model="formData[field.value]"
      />
      <FieldBirth
        v-else-if="field.class === 'birth'"
        :key="field.value + idx"
        :ref="`field_${field.value}`"
        :field="field"
        v-model="formData[field.value]"
      />
      <FieldArea
        v-else-if="field.class === 'area'"
        :key="field.value + idx"
        :ref="`field_${field.value}`"
        :field="field"
        v-model="formData[field.value]"
      />
    </template>
  </div>
</template>

<script>
import FieldText     from './fields/FieldText.vue'
import FieldNumber   from './fields/FieldNumber.vue'
import FieldRadio    from './fields/FieldRadio.vue'
import FieldCheckbox from './fields/FieldCheckbox.vue'
import FieldDatetime from './fields/FieldDatetime.vue'
import FieldSelcet   from './fields/FieldSelcet.vue'
import FieldFileList from './fields/FieldFileList.vue'
import FieldFile     from './fields/FieldFile.vue'
import FieldBirth    from './fields/FieldBirth.vue'
import FieldArea     from './fields/FieldArea.vue'

export default {
  name: 'DynamicForm',
  components: { FieldText, FieldNumber, FieldRadio, FieldCheckbox, FieldDatetime, FieldSelcet, FieldFileList, FieldFile, FieldBirth, FieldArea },
  props: {
    fields: { type: Array, default: () => [] }
  },
  data () {
    return { formData: {} }
  },
  watch: {
    fields: {
      immediate: true,
      handler (fields) {
        const data = {}
        ;(fields || []).forEach(f => {
          if (f.class === 'checkbox') data[f.value] = []
          else if (f.class === 'selcet') data[f.value] = []
          else if (f.class === 'birth') data[f.value] = {}
          else if (f.class === 'area') data[f.value] = {}
          else if (f.class === 'fileList') data[f.value] = []
          else data[f.value] = ''
        })
        this.formData = data
      }
    }
  },
  methods: {
    /**
     * 校驗所有必填欄位
     * 返回第一個錯誤消息字符串，或空字符串（通過）
     */
    validate () {
      for (const field of this.fields) {
        const label = field.label || ''
        const val = this.formData[field.value]
        const ref = this.$refs[`field_${field.value}`]
        const refComp = Array.isArray(ref) ? ref[0] : ref

        // fileList / file：無論必填與否，只要有 uploading/error 狀態就攔截
        if ((field.class === 'fileList' || field.class === 'file') && refComp) {
          if (refComp.hasUploading && refComp.hasUploading()) return `${label} 正在上傳中，請稍候…`
          if (refComp.hasError && refComp.hasError()) return `${label} 存在上傳失敗的附件，請重試或刪除`
        }

        if (!field.fill_status) continue

        // 按類型校驗
        switch (field.class) {
          case 'text':
          case 'number':
          case 'radio':
          case 'datetime':
            if (val === null || val === undefined || String(val).trim() === '') return `請填寫${label}！`
            break
          case 'checkbox':
          case 'selcet':
          case 'fileList':
            if (!val || !val.length) return `請選擇${label}！`
            break
          case 'file':
            if (!val) return `請上傳${label}！`
            break
          case 'birth': {
            const b = val || {}
            if (!b.bir_year || !b.bir_month || !b.bir_day) return `請選擇${label}！`
            break
          }
          case 'area': {
            const a = val || {}
            if (!a.region_name || !a.central_district_name || !a.xq_name) return `請選擇${label}（大中小區）！`
            break
          }
        }
      }
      return ''
    },

    /**
     * 收集所有欄位值
     * 系統欄位（type=0）→ params 平鋪
     * 自定義欄位（type=1）→ customData（混合類型，最後 JSON.stringify）
     */
    collectValue () {
      const params = {}
      const customData = {}

      for (const field of this.fields) {
        const val = this.formData[field.value]
        const bag = field.type === 1 ? customData : params

        switch (field.class) {
          case 'birth':
            // 系統欄位，平鋪三個 key 到 params
            params.bir_year  = (val && val.bir_year)  || ''
            params.bir_month = (val && val.bir_month) || ''
            params.bir_day   = (val && val.bir_day)   || ''
            break
          case 'area':
            // 系統欄位，平鋪 areaForm 格式到 params
            if (val) Object.assign(params, val)
            break
          case 'text':
            bag[field.value] = typeof val === 'string' ? val.trim() : (val || '')
            break
          default:
            bag[field.value] = val !== undefined ? val : ''
        }
      }

      return { params, customData }
    },

    /** 重置所有欄位 */
    reset () {
      const data = {}
      ;(this.fields || []).forEach(f => {
        if (f.class === 'checkbox') data[f.value] = []
        else if (f.class === 'selcet') data[f.value] = []
        else if (f.class === 'birth') data[f.value] = {}
        else if (f.class === 'area') data[f.value] = {}
        else if (f.class === 'fileList') data[f.value] = []
        else data[f.value] = ''
      })
      this.formData = data
      // 調各組件的 reset 方法（birth/area/fileList/file/selcet 有內部狀態）
      this.fields.forEach(f => {
        const ref = this.$refs[`field_${f.value}`]
        const comp = Array.isArray(ref) ? ref[0] : ref
        if (comp && typeof comp.reset === 'function') comp.reset()
      })
    }
  }
}
</script>
